import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Main from "./Main";
import { makeCompanyNav } from "./nav";
import { request, API_BASE, getCsrfToken } from "../services/server";
import useWorkspaceLifecycle from "../hooks/useWorkspaceLifecycle";
import {
  WorkspaceLockOverlay,
  WorkspaceCloseBanner,
} from "./components/WorkspaceLifecycleUI";
import BuyerSuggestionPanel from "./components/BuyerSuggestionPanel";
import "./css/pipeline.css";

/**
 * Pipeline — per-buyer progression with filters, drag-and-drop reorder,
 * per-buyer drawer (with activity feed, signing, invite-to-portal), CSV
 * export, and Kanban/list view toggle.
 *
 * Manual advancement only — the broker moves each buyer through stages.
 */

const BUYER_STAGES = [
  { value: "identified",  label: "Identifierad",  tone: "neutral" },
  { value: "contacted",   label: "Kontaktad",     tone: "neutral" },
  { value: "nda_sent",    label: "NDA skickat",   tone: "progress" },
  { value: "nda_signed",  label: "NDA signerat",  tone: "progress" },
  { value: "im_sent",     label: "PM skickat",    tone: "progress" },
  { value: "interested",  label: "Intresserad",   tone: "good" },
  { value: "loi",         label: "LOI",           tone: "good" },
  { value: "dd",          label: "DD pågår",      tone: "active" },
  { value: "negotiating", label: "Förhandling",   tone: "active" },
  { value: "offer",       label: "Bud",           tone: "good" },
  { value: "selected",    label: "Vald köpare",   tone: "good" },
];

const TERMINAL_STAGES = [
  { value: "declined",  label: "Avböjt",         tone: "bad" },
  { value: "withdrawn", label: "Dragit sig ur",  tone: "bad" },
];

const ALL_STAGES = [...BUYER_STAGES, ...TERMINAL_STAGES];

function stageByValue(v) { return ALL_STAGES.find((s) => s.value === v); }
function stageIndex(v) { return BUYER_STAGES.findIndex((s) => s.value === v); }

const TONE_COLORS = {
  neutral:  { bg: "#F5F5F4", fg: "#57534E", border: "#E7E5E4" },
  progress: { bg: "#FEF3C7", fg: "#92400E", border: "#FDE68A" },
  good:     { bg: "#DCFCE7", fg: "#166534", border: "#BBF7D0" },
  active:   { bg: "#DBEAFE", fg: "#1E40AF", border: "#BFDBFE" },
  bad:      { bg: "#FEE2E2", fg: "#991B1B", border: "#FECACA" },
};

const PRIORITY_BORDER = {
  high:   "#DC2626",
  medium: "#A8A29E",
  low:    "#E7E5E4",
};

// Stages that advance automatically when their trigger fires (NDA send,
// webhook signing). The "Glöm inte..." hints are gone — auto-advance covers
// those moments. Hints for stages that still need broker judgement stay.
const STAGE_ADVANCE_HINTS = {
  nda_signed: "Dags att skicka PM (prospektmaterial).",
  im_sent: "Invänta intresse. Gå vidare om köparen är intresserad.",
  loi: "Ge köparen åtkomst till datarum och starta due diligence.",
  dd: "Förhandla pris/villkor när DD är klar.",
};

export default function Pipeline() {
  const { company } = useParams();
  const nav = makeCompanyNav(company);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [showAddBuyer, setShowAddBuyer] = useState(false);
  const [showImportScb, setShowImportScb] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [newBuyer, setNewBuyer] = useState({
    buyer_company_name: "",
    contact_name: "",
    contact_email: "",
  });
  const [adding, setAdding] = useState(false);

  const [drawerBuyerId, setDrawerBuyerId] = useState(null);

  const [signingTemplates, setSigningTemplates] = useState([]);
  const [signingDocs, setSigningDocs] = useState([]);

  // Filters
  const [filter, setFilter] = useState("active"); // active | hot | offers | stale | all | terminal
  const [viewMode, setViewMode] = useState("list"); // list | kanban

  // Drag and drop
  const [draggedId, setDraggedId] = useState(null);

  const loadPipeline = useCallback(async () => {
    if (!company) return;
    try {
      const d = await request(`/users/workspaces/${company}/pipeline/`);
      setData(d);
    } catch (e) {
      setError(e?.body?.detail || e?.message || "Kunde inte ladda pipeline.");
    } finally {
      setLoading(false);
    }
  }, [company]);

  useEffect(() => { loadPipeline(); }, [loadPipeline]);

  const loadSigning = useCallback(async () => {
    if (!company) return;
    try {
      const [tpls, docs] = await Promise.all([
        request(`/signing/templates/?workspace=${encodeURIComponent(company)}`).catch(() => []),
        request(`/signing/${company}/documents/`).catch(() => []),
      ]);
      setSigningTemplates(Array.isArray(tpls) ? tpls : []);
      setSigningDocs(Array.isArray(docs) ? docs : []);
    } catch {}
  }, [company]);

  useEffect(() => { loadSigning(); }, [loadSigning]);

  async function addBuyer() {
    if (!newBuyer.buyer_company_name.trim()) return;
    setAdding(true);
    try {
      await request(`/users/workspaces/${company}/buyers/`, {
        method: "POST",
        body: newBuyer,
      });
      setNewBuyer({ buyer_company_name: "", contact_name: "", contact_email: "" });
      setShowAddBuyer(false);
      await loadPipeline();
    } catch (e) {
      alert(e?.body?.detail || "Kunde inte lägga till köpare.");
    }
    setAdding(false);
  }

  async function updateBuyer(buyerId, patch) {
    try {
      await request(`/users/workspaces/${company}/buyers/${buyerId}/`, {
        method: "PATCH",
        body: patch,
      });
      await loadPipeline();
    } catch (e) {
      alert(e?.body?.detail || "Uppdatering misslyckades.");
    }
  }

  async function removeBuyer(buyerId) {
    if (!window.confirm("Ta bort denna köpare från pipelinen?")) return;
    try {
      await request(`/users/workspaces/${company}/buyers/${buyerId}/`, {
        method: "DELETE",
      });
      if (drawerBuyerId === buyerId) setDrawerBuyerId(null);
      await loadPipeline();
    } catch (e) {
      alert(e?.body?.detail || "Kunde inte ta bort köpare.");
    }
  }

  async function sendForSigning(buyer, templateId) {
    if (!buyer.contact_email) {
      alert("Köparen saknar e-postadress. Lägg till en först.");
      return;
    }
    // Name must be a real first+last; backend rejects email-as-name too,
    // but we surface the issue earlier here so the broker doesn't have
    // to wait for a round-trip.
    const fullName = (buyer.contact_name || "").trim();
    if (!fullName || fullName.split(/\s+/).length < 2 || fullName.includes("@")) {
      alert(
        "Köparen behöver ett komplett för- och efternamn innan NDA/LOI kan skickas. " +
        "Lägg till det under Kontakt och spara."
      );
      return;
    }
    try {
      // Step 1: render the document from the template + recipient details.
      // The backend creates a SigningDocument with status="ready" and a
      // generated PDF, but does NOT send it yet.
      const created = await request(`/signing/${company}/documents/`, {
        method: "POST",
        body: {
          template_id: templateId,
          recipient_email: buyer.contact_email,
          recipient_name: fullName,
          recipient_personnummer: buyer.contact_personnummer || "",
          recipient_type: "buyer",
        },
      });

      // Step 2: actually send via DocuSeal. The "workspace NDA" shortcut
      // (template_id like "workspace_nda_<orgnr>") already calls DocuSeal
      // during creation and returns status=sent; for those we skip the
      // explicit /send/ call to avoid double-submitting.
      const alreadySent = created?.status === "sent";
      if (!alreadySent && created?.id && created?.can_send !== false) {
        await request(`/signing/${company}/documents/${created.id}/send/`, {
          method: "POST",
        });
      }

      // Auto-advance pipeline stage: when an NDA goes out, the buyer should
      // visibly move to "nda_sent". Skip if they're already past it
      // (broker re-sent for some reason; don't regress signed → sent).
      const advanceFrom = ["identified", "contacted"];
      if (advanceFrom.includes(buyer.status)) {
        try {
          await updateBuyer(buyer.id, { status: "nda_sent" });
        } catch {
          // non-fatal; loadSigning() still refreshes the rest
        }
      }

      await loadSigning();
    } catch (e) {
      alert(e?.body?.detail || "Kunde inte skicka för signering.");
    }
  }

  async function sendPMToBuyer(buyer) {
    if (!buyer.contact_email) {
      alert("Köparen saknar e-postadress.");
      return;
    }
    if (!window.confirm(`Skicka PM till ${buyer.contact_email}?`)) return;
    try {
      await request(`/im-studio/company/${company}/send-pm/`, {
        method: "POST",
        body: { buyer_id: buyer.id },
      });
      await loadPipeline();
    } catch (e) {
      // Backend returns code: "pm_not_finalized" + Swedish detail when the
      // PM hasn't been marked finalized yet — surface it as a clear alert
      // so the broker knows where to go.
      const detail = e?.body?.detail || e?.message || "Kunde inte skicka PM.";
      alert(detail);
    }
  }

  async function inviteToBuyerPortal(buyer) {
    if (!buyer.contact_email) {
      alert("Köparen saknar e-postadress.");
      return;
    }
    if (!window.confirm(`Skicka inloggning till köparportalen till ${buyer.contact_email}?`)) return;
    try {
      await request(`/users/buyer-invitations/`, {
        method: "POST",
        body: {
          workspace_key: company,
          email: buyer.contact_email,
          buyer_company_name: buyer.buyer_company_name,
          modules: ["valuation", "dataroom", "dd", "im"],
        },
      });
      // Flip has_dataroom_access so the row indicator updates
      if (!buyer.has_dataroom_access) {
        await updateBuyer(buyer.id, { has_dataroom_access: true });
      }
      // Success popup removed — broker confirmation comes from the
      // updated dataroom-access badge on the row.
    } catch (e) {
      alert(e?.body?.detail || "Kunde inte skicka inbjudan.");
    }
  }

  async function reorderBuyers(newOrder) {
    try {
      await request(`/users/workspaces/${company}/buyers/reorder/`, {
        method: "POST",
        body: { order: newOrder },
      });
      await loadPipeline();
    } catch (e) {
      alert(e?.body?.detail || "Kunde inte ändra ordning.");
    }
  }

  async function importFromScb(companies) {
    if (!companies?.length) return { imported: 0 };
    try {
      const res = await request(`/users/workspaces/${company}/buyers/import/`, {
        method: "POST",
        body: { companies, source: "scb" },
      });
      await loadPipeline();
      return res;
    } catch (e) {
      alert(e?.body?.detail || "Importen misslyckades.");
      return { imported: 0 };
    }
  }

  function exportCsv() {
    // Use window.open with cookies via credentials
    const url = `${API_BASE}/users/workspaces/${company}/pipeline/export.csv`;
    // Use a hidden form so cookies flow naturally
    const a = document.createElement("a");
    a.href = url;
    a.setAttribute("download", "");
    a.click();
  }

  // Drag handlers for list reorder
  const handleDragStart = (id) => () => setDraggedId(id);
  const handleDragOver = (e) => e.preventDefault();
  const handleDrop = (targetId) => (e) => {
    e.preventDefault();
    if (!draggedId || draggedId === targetId || !data) return;
    const ids = data.buyers.map((b) => b.id);
    const fromIdx = ids.indexOf(draggedId);
    const toIdx = ids.indexOf(targetId);
    if (fromIdx === -1 || toIdx === -1) return;
    const next = [...ids];
    const [moved] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moved);
    setDraggedId(null);
    // Optimistic reorder for snappy UX
    setData((prev) => ({
      ...prev,
      buyers: next.map((id) => prev.buyers.find((b) => b.id === id)).filter(Boolean),
    }));
    reorderBuyers(next);
  };

  const drawerBuyer = useMemo(
    () => data?.buyers?.find((b) => b.id === drawerBuyerId) || null,
    [data, drawerBuyerId]
  );

  // Filtered buyers
  const filteredBuyers = useMemo(() => {
    if (!data?.buyers) return [];
    const STALE_DAYS = 14;
    const now = Date.now();
    return data.buyers.filter((b) => {
      if (filter === "all") return true;
      if (filter === "terminal") return b.is_terminal;
      if (filter === "active") return !b.is_terminal;
      if (filter === "hot") {
        const hotStages = ["interested", "loi", "dd", "negotiating", "offer", "selected"];
        return !b.is_terminal && hotStages.includes(b.status);
      }
      if (filter === "offers") return b.offer_amount != null && !b.is_terminal;
      if (filter === "stale") {
        if (b.is_terminal) return false;
        if (!b.updated_at) return false;
        const age = (now - new Date(b.updated_at).getTime()) / (1000 * 60 * 60 * 24);
        return age > STALE_DAYS;
      }
      return true;
    });
  }, [data, filter]);

  const { workspace: wsLifecycle, reload: reloadLifecycle } = useWorkspaceLifecycle(company);

  if (loading) return <Main navItems={nav}><div className="pl-loading">Laddar pipeline…</div></Main>;
  if (error)   return <Main navItems={nav}><div className="pl-error">{error}</div></Main>;
  if (!data)   return <Main navItems={nav}><div className="pl-empty">Ingen data.</div></Main>;

  const furthestIdx = data.furthest_stage_index ?? -1;

  return (
    <Main navItems={nav}>
      <WorkspaceCloseBanner workspace={wsLifecycle} />
      <WorkspaceLockOverlay workspace={wsLifecycle} onActivate={reloadLifecycle}>
      <div className="pl-root">
        {/* ── Header ── */}
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 16 }}>
          <div>
            <h1 className="pl-title" style={{ margin: 0 }}>Pipeline</h1>
            <p style={{ fontSize: 13, color: "#78716C", margin: "4px 0 0" }}>
              Varje köpare rör sig individuellt. Klicka på en rad för detaljer.
            </p>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <span className="pl-badge">{data.active_buyers} aktiva av {data.buyer_count}</span>
            <button
              className="pl-btn pl-btn--ghost"
              type="button"
              onClick={() => setShowSuggestions((v) => !v)}
              style={btnStyle(false)}
            >
              ⌕ {showSuggestions ? "Dölj förslag" : "Hitta köpare"}
            </button>
            <button className="pl-add-btn" onClick={() => setShowAddBuyer((v) => !v)}>
              + Lägg till köpare
            </button>
          </div>
        </div>

        {/* Auto-scored buyer suggestions — SCB-backed search + matcher. */}
        {showSuggestions && (
          <div style={{ marginBottom: 20 }}>
            <BuyerSuggestionPanel
              workspaceKey={company}
              onApprove={loadPipeline}
            />
          </div>
        )}

        {/* ── Top stepper ── */}
        <div
          style={{
            background: "#FAFAF9",
            border: "1px solid #E7E5E4",
            borderRadius: 12,
            padding: "16px 18px",
            marginBottom: 16,
          }}
        >
          <div style={{ fontSize: 11, color: "#78716C", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 10, fontWeight: 600 }}>
            Affärens längsta framsteg
          </div>
          <StepperBar stages={BUYER_STAGES} furthestIdx={furthestIdx} />
        </div>

        {/* ── Filter + view toggle bar ── */}
        <div style={{
          display: "flex",
          gap: 8,
          alignItems: "center",
          marginBottom: 16,
          flexWrap: "wrap",
        }}>
          <div style={{ display: "flex", gap: 4, background: "#F5F5F4", padding: 3, borderRadius: 8 }}>
            {[
              { k: "active", label: "Aktiva" },
              { k: "hot", label: "Heta" },
              { k: "offers", label: "Med bud" },
              { k: "stale", label: "Behöver uppföljning" },
              { k: "terminal", label: "Avslutade" },
              { k: "all", label: "Alla" },
            ].map((f) => (
              <button
                key={f.k}
                onClick={() => setFilter(f.k)}
                style={{
                  padding: "6px 12px",
                  fontSize: 12,
                  fontWeight: 600,
                  border: "none",
                  borderRadius: 6,
                  cursor: "pointer",
                  background: filter === f.k ? "#fff" : "transparent",
                  color: filter === f.k ? "#1C1917" : "#78716C",
                  boxShadow: filter === f.k ? "0 1px 3px rgba(0,0,0,0.06)" : "none",
                }}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div style={{ flex: 1 }} />
          <div style={{ display: "flex", gap: 4, background: "#F5F5F4", padding: 3, borderRadius: 8 }}>
            <button
              onClick={() => setViewMode("list")}
              style={viewToggleStyle(viewMode === "list")}
              title="Listvy"
            >
              ☰ Lista
            </button>
            <button
              onClick={() => setViewMode("kanban")}
              style={viewToggleStyle(viewMode === "kanban")}
              title="Kanbanvy"
            >
              ⊞ Kanban
            </button>
          </div>
        </div>

        {/* Add buyer form */}
        {showAddBuyer && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr auto",
              gap: 8,
              padding: 14,
              background: "#FAFAF9",
              border: "1px solid #E7E5E4",
              borderRadius: 10,
              marginBottom: 16,
            }}
          >
            <input
              placeholder="Företagsnamn *"
              value={newBuyer.buyer_company_name}
              onChange={(e) => setNewBuyer((p) => ({ ...p, buyer_company_name: e.target.value }))}
              className="pl-input"
              style={inputStyle}
            />
            <input
              placeholder="Kontaktperson"
              value={newBuyer.contact_name}
              onChange={(e) => setNewBuyer((p) => ({ ...p, contact_name: e.target.value }))}
              style={inputStyle}
            />
            <input
              type="email"
              placeholder="E-post"
              value={newBuyer.contact_email}
              onChange={(e) => setNewBuyer((p) => ({ ...p, contact_email: e.target.value }))}
              style={inputStyle}
            />
            <button
              onClick={addBuyer}
              disabled={adding || !newBuyer.buyer_company_name.trim()}
              style={btnStyle(adding || !newBuyer.buyer_company_name.trim(), true)}
            >
              {adding ? "Sparar…" : "Spara"}
            </button>
          </div>
        )}

        {/* ── Buyer list or kanban ── */}
        {filteredBuyers.length === 0 ? (
          <EmptyState filter={filter} hasAny={data.buyers.length > 0} />
        ) : viewMode === "kanban" ? (
          <KanbanBoard
            buyers={filteredBuyers}
            signingDocs={signingDocs}
            onOpen={(id) => setDrawerBuyerId(id)}
            onStatusChange={(id, status) => updateBuyer(id, { status })}
          />
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {filteredBuyers.map((b) => (
              <BuyerRow
                key={b.id}
                buyer={b}
                signingDocs={signingDocs.filter((d) => d.recipient_email === b.contact_email)}
                onOpen={() => setDrawerBuyerId(b.id)}
                onRemove={() => removeBuyer(b.id)}
                draggable
                onDragStart={handleDragStart(b.id)}
                onDragOver={handleDragOver}
                onDrop={handleDrop(b.id)}
                isDragging={draggedId === b.id}
              />
            ))}
          </div>
        )}

        {/* "Senaste aktivitet" moved out of Pipeline — same data is available
            in the Logg page where it belongs. */}
      </div>

      {/* Buyer detail drawer */}
      {drawerBuyer && (
        <BuyerDrawer
          company={company}
          buyer={drawerBuyer}
          signingTemplates={signingTemplates}
          signingDocs={signingDocs.filter((d) => d.recipient_email === drawerBuyer.contact_email)}
          onClose={() => setDrawerBuyerId(null)}
          onUpdate={(patch) => updateBuyer(drawerBuyer.id, patch)}
          onRemove={() => removeBuyer(drawerBuyer.id)}
          onSendSigning={(templateId) => sendForSigning(drawerBuyer, templateId)}
          onInviteToPortal={() => inviteToBuyerPortal(drawerBuyer)}
          onSendPM={() => sendPMToBuyer(drawerBuyer)}
        />
      )}

      {showImportScb && (
        <ImportScbModal
          existingNames={new Set(data.buyers.map((b) => b.buyer_company_name.toLowerCase()))}
          onImport={importFromScb}
          onClose={() => setShowImportScb(false)}
        />
      )}
      </WorkspaceLockOverlay>
    </Main>
  );
}

/* ─────────────────────────────────────────────────────────
   Stepper bar
───────────────────────────────────────────────────────── */

function StepperBar({ stages, furthestIdx, compact = false }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 0 }}>
      {stages.map((stage, idx) => {
        const isCompleted = idx < furthestIdx;
        const isCurrent = idx === furthestIdx;
        return (
          <div
            key={stage.value}
            style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", position: "relative", minWidth: 0 }}
          >
            {idx > 0 && (
              <div
                style={{
                  position: "absolute",
                  left: "-50%",
                  right: "50%",
                  top: compact ? 7 : 11,
                  height: 2,
                  background: idx <= furthestIdx ? "#57534E" : "#E7E5E4",
                }}
              />
            )}
            <div
              style={{
                width: compact ? 16 : 24,
                height: compact ? 16 : 24,
                borderRadius: "50%",
                background: isCompleted ? "#57534E" : isCurrent ? "#fff" : "#FAFAF9",
                border: `2px solid ${isCompleted || isCurrent ? "#57534E" : "#D6D3D1"}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: compact ? 9 : 11,
                color: isCompleted ? "#fff" : "#57534E",
                fontWeight: 700,
                position: "relative",
                zIndex: 1,
              }}
              title={stage.label}
            >
              {isCompleted ? "✓" : ""}
            </div>
            {!compact && (
              <div
                style={{
                  fontSize: 10,
                  fontWeight: isCurrent ? 700 : 500,
                  color: isCompleted || isCurrent ? "#1C1917" : "#A8A29E",
                  marginTop: 4,
                  textAlign: "center",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: "100%",
                }}
              >
                {stage.label}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────
   Buyer row (list view)
───────────────────────────────────────────────────────── */

function BuyerRow({ buyer, signingDocs, onOpen, onRemove, draggable, onDragStart, onDragOver, onDrop, isDragging }) {
  const stage = stageByValue(buyer.status);
  const isTerminal = buyer.is_terminal;
  const tone = TONE_COLORS[stage?.tone || "neutral"];
  const priorityColor = PRIORITY_BORDER[buyer.priority] || PRIORITY_BORDER.medium;

  const hasEmail = !!buyer.contact_email;
  const pendingDocs = signingDocs.filter((d) => d.status !== "signed").length;
  const signedDocs = signingDocs.filter((d) => d.status === "signed").length;

  return (
    <div
      draggable={draggable}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onClick={onOpen}
      style={{
        background: "#fff",
        border: "1px solid #E7E5E4",
        borderLeft: `4px solid ${priorityColor}`,
        borderRadius: 12,
        padding: "14px 16px",
        cursor: "pointer",
        transition: "all 0.15s",
        opacity: isTerminal ? 0.6 : isDragging ? 0.4 : 1,
      }}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#A8A29E"; e.currentTarget.style.borderLeftColor = priorityColor; e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.04)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#E7E5E4"; e.currentTarget.style.borderLeftColor = priorityColor; e.currentTarget.style.boxShadow = "none"; }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 12 }}>
        {/* Drag handle */}
        <div
          style={{ color: "#D6D3D1", fontSize: 16, cursor: "grab", flexShrink: 0, lineHeight: 1 }}
          title="Dra för att ändra ordning"
          onClick={(e) => e.stopPropagation()}
        >
          ⋮⋮
        </div>

        {/* Company + contact */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 15, color: "#1C1917", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {buyer.buyer_company_name}
          </div>
          <div style={{ fontSize: 12, color: "#78716C", marginTop: 2, display: "flex", gap: 8, alignItems: "center" }}>
            {buyer.contact_name && <span>{buyer.contact_name}</span>}
            {buyer.contact_name && hasEmail && <span>·</span>}
            {hasEmail ? (
              <span>{buyer.contact_email}</span>
            ) : (
              <span style={{ color: "#D97706", fontStyle: "italic" }}>Ingen e-post</span>
            )}
          </div>
        </div>

        <span
          style={{
            padding: "4px 10px",
            background: tone.bg,
            color: tone.fg,
            border: `1px solid ${tone.border}`,
            borderRadius: 12,
            fontSize: 11,
            fontWeight: 600,
            whiteSpace: "nowrap",
          }}
        >
          {stage?.label || buyer.status}
        </span>

        {(pendingDocs > 0 || signedDocs > 0) && (
          <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11 }}>
            {signedDocs > 0 && (
              <span style={{ padding: "2px 6px", background: "#DCFCE7", color: "#166534", borderRadius: 6 }}>
                ✓ {signedDocs}
              </span>
            )}
            {pendingDocs > 0 && (
              <span style={{ padding: "2px 6px", background: "#FEF3C7", color: "#92400E", borderRadius: 6 }}>
                ⧗ {pendingDocs}
              </span>
            )}
          </div>
        )}

        {buyer.nda_signed && (
          <span style={{
            padding: "2px 8px",
            background: "#DCFCE7",
            color: "#166534",
            borderRadius: 10,
            fontSize: 10,
            fontWeight: 700,
          }}>
            NDA ✓
          </span>
        )}

        {/* "Buyer opened the NDA but hasn't signed" — surfaces form.viewed
            webhook events so the broker sees passive engagement. */}
        {!buyer.nda_signed && signingDocs.some((d) => d.viewed_at && d.status !== "signed") && (
          <span
            title="Köparen har öppnat dokumentet men inte signerat ännu"
            style={{
              padding: "2px 8px",
              background: "#EFF6FF",
              color: "#1D4ED8",
              borderRadius: 10,
              fontSize: 10,
              fontWeight: 700,
            }}
          >
            👁 Öppnad
          </span>
        )}

        {buyer.offer_amount && (
          <div style={{ fontSize: 12, fontWeight: 600, color: "#166534" }}>
            {Number(buyer.offer_amount).toLocaleString("sv-SE")} SEK
          </div>
        )}

        <button
          onClick={(e) => { e.stopPropagation(); onRemove(); }}
          title="Ta bort köpare"
          style={{
            background: "none",
            border: "none",
            fontSize: 18,
            color: "#A8A29E",
            cursor: "pointer",
            padding: "4px 6px",
            borderRadius: 4,
          }}
          onMouseEnter={(e) => { e.currentTarget.style.color = "#DC2626"; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = "#A8A29E"; }}
        >
          ✕
        </button>
      </div>

      {!isTerminal && <StepperBar stages={BUYER_STAGES} furthestIdx={buyer.stage_index} compact />}
      {isTerminal && (
        <div style={{ fontSize: 12, color: "#991B1B", fontStyle: "italic" }}>
          Denna köpare har {stage?.label?.toLowerCase() || "avslutats"}.
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────
   Kanban board view
───────────────────────────────────────────────────────── */

function KanbanBoard({ buyers, signingDocs, onOpen, onStatusChange }) {
  const [dragBuyerId, setDragBuyerId] = useState(null);

  return (
    <div style={{ display: "flex", gap: 12, overflowX: "auto", paddingBottom: 12 }}>
      {BUYER_STAGES.map((stage) => {
        const colBuyers = buyers.filter((b) => b.status === stage.value);
        const tone = TONE_COLORS[stage.tone];
        return (
          <div
            key={stage.value}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              if (dragBuyerId) {
                onStatusChange(dragBuyerId, stage.value);
                setDragBuyerId(null);
              }
            }}
            style={{
              minWidth: 260,
              maxWidth: 260,
              background: "#FAFAF9",
              border: `1px solid ${tone.border}`,
              borderTop: `3px solid ${tone.fg}`,
              borderRadius: 10,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div style={{
              padding: "10px 12px",
              borderBottom: `1px solid ${tone.border}`,
              fontSize: 12,
              fontWeight: 700,
              color: tone.fg,
              textTransform: "uppercase",
              letterSpacing: 0.3,
              display: "flex",
              justifyContent: "space-between",
            }}>
              <span>{stage.label}</span>
              <span style={{ background: "#fff", padding: "1px 8px", borderRadius: 10, fontSize: 11 }}>
                {colBuyers.length}
              </span>
            </div>
            <div style={{ padding: 8, display: "flex", flexDirection: "column", gap: 6, minHeight: 80 }}>
              {colBuyers.map((b) => {
                const pc = PRIORITY_BORDER[b.priority] || PRIORITY_BORDER.medium;
                return (
                  <div
                    key={b.id}
                    draggable
                    onDragStart={() => setDragBuyerId(b.id)}
                    onDragEnd={() => setDragBuyerId(null)}
                    onClick={() => onOpen(b.id)}
                    style={{
                      background: "#fff",
                      border: "1px solid #E7E5E4",
                      borderLeft: `3px solid ${pc}`,
                      borderRadius: 8,
                      padding: "8px 10px",
                      cursor: "pointer",
                      fontSize: 12,
                      opacity: dragBuyerId === b.id ? 0.4 : 1,
                    }}
                  >
                    <div style={{ fontWeight: 600, color: "#1C1917", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {b.buyer_company_name}
                    </div>
                    {b.contact_email && (
                      <div style={{ color: "#78716C", fontSize: 10, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {b.contact_email}
                      </div>
                    )}
                    {b.offer_amount && (
                      <div style={{ color: "#166534", fontWeight: 600, marginTop: 4 }}>
                        {Number(b.offer_amount).toLocaleString("sv-SE")} SEK
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────
   Empty state
───────────────────────────────────────────────────────── */

function EmptyState({ filter, hasAny }) {
  const msg = !hasAny
    ? "Inga köpare tillagda. Klicka på \"+ Lägg till köpare\" för att börja."
    : filter === "stale"
      ? "Inga köpare behöver uppföljning just nu."
      : filter === "hot"
        ? "Inga heta köpare just nu."
        : filter === "offers"
          ? "Inga bud har mottagits."
          : "Inga köpare matchar filtret.";

  return (
    <div
      style={{
        padding: 40,
        textAlign: "center",
        color: "#78716C",
        background: "#FAFAF9",
        border: "1px dashed #E7E5E4",
        borderRadius: 12,
      }}
    >
      {msg}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────
   Buyer detail drawer
───────────────────────────────────────────────────────── */

function BuyerDrawer({
  company,
  buyer,
  signingTemplates,
  signingDocs,
  onClose,
  onUpdate,
  onRemove,
  onSendSigning,
  onInviteToPortal,
  onSendPM,
}) {
  const [email, setEmail] = useState(buyer.contact_email || "");
  const [name, setName] = useState(buyer.contact_name || "");
  const [personnummer, setPersonnummer] = useState(buyer.contact_personnummer || "");
  const [phone, setPhone] = useState(buyer.contact_phone || "");
  const [notes, setNotes] = useState(buyer.notes || "");
  const [offerAmount, setOfferAmount] = useState(buyer.offer_amount || "");
  const [priority, setPriority] = useState(buyer.priority || "medium");
  const [savingContact, setSavingContact] = useState(false);
  // Per-doc-type template selection: NDA, LOI, and a catch-all for other types.
  const [selectedTemplateByType, setSelectedTemplateByType] = useState({});
  const [activity, setActivity] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);

  useEffect(() => {
    setEmail(buyer.contact_email || "");
    setName(buyer.contact_name || "");
    setPersonnummer(buyer.contact_personnummer || "");
    setPhone(buyer.contact_phone || "");
    setNotes(buyer.notes || "");
    setOfferAmount(buyer.offer_amount || "");
    setPriority(buyer.priority || "medium");
  }, [buyer.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load activity when drawer opens for a new buyer
  useEffect(() => {
    if (!buyer?.id || !company) return;
    setActivityLoading(true);
    request(`/users/workspaces/${company}/buyers/${buyer.id}/activity/`)
      .then((d) => setActivity(d?.events || []))
      .catch(() => setActivity([]))
      .finally(() => setActivityLoading(false));
  }, [buyer.id, company]);

  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  const currentIdx = buyer.stage_index;
  const canAdvance = currentIdx >= 0 && currentIdx < BUYER_STAGES.length - 1 && !buyer.is_terminal;
  const canRevert = currentIdx > 0 && !buyer.is_terminal;

  async function saveContact() {
    setSavingContact(true);
    try {
      await onUpdate({
        contact_email: email,
        contact_name: name,
        contact_personnummer: personnummer,
        contact_phone: phone,
      });
    } finally {
      setSavingContact(false);
    }
  }

  async function savePriority(newPrio) {
    setPriority(newPrio);
    await onUpdate({ priority: newPrio });
  }

  async function saveNotes() {
    await onUpdate({ notes });
  }

  async function saveOffer() {
    const val = String(offerAmount).trim();
    await onUpdate({ offer_amount: val ? parseInt(val, 10) : null });
  }

  function advance() {
    if (!canAdvance) return;
    const next = BUYER_STAGES[currentIdx + 1];
    onUpdate({ status: next.value });
  }

  function revert() {
    if (!canRevert) return;
    const prev = BUYER_STAGES[currentIdx - 1];
    onUpdate({ status: prev.value });
  }

  function setTerminal(status) {
    onUpdate({ status });
  }

  // Only surface the "skicka PM" hint when the NDA is ACTUALLY signed
  // (DealBuyer.nda_signed flag from the DocuSeal webhook). Broker can
  // manually advance the status to nda_signed without the contract being
  // signed in DocuSeal, and we don't want the hint to fire prematurely.
  let advanceHint = STAGE_ADVANCE_HINTS[buyer.status];
  if (buyer.status === "nda_signed" && !buyer.nda_signed) {
    advanceHint = null;
  }

  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", zIndex: 1000, animation: "plFade 0.2s ease-out" }} />
      <aside
        role="dialog"
        aria-modal="true"
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          bottom: 0,
          width: "min(680px, 100vw)",
          background: "#fff",
          boxShadow: "-4px 0 24px rgba(0,0,0,0.15)",
          zIndex: 1001,
          display: "flex",
          flexDirection: "column",
          animation: "plSlide 0.25s ease-out",
        }}
      >
        {/* Header */}
        <header style={{ padding: "20px 24px", borderBottom: "1px solid #E7E5E4", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: 11, color: "#78716C", letterSpacing: 0.5, marginBottom: 4 }}>KÖPARE</div>
              <h2 style={{ fontSize: 20, fontWeight: 600, color: "#1C1917", margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={buyer.buyer_company_name}>
                {buyer.buyer_company_name}
              </h2>
              <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
                {["high", "medium", "low"].map((p) => (
                  <button
                    key={p}
                    onClick={() => savePriority(p)}
                    style={{
                      padding: "2px 10px",
                      fontSize: 11,
                      fontWeight: 600,
                      border: `1px solid ${priority === p ? PRIORITY_BORDER[p] : "#E7E5E4"}`,
                      background: priority === p ? PRIORITY_BORDER[p] : "#fff",
                      color: priority === p ? "#fff" : "#78716C",
                      borderRadius: 10,
                      cursor: "pointer",
                    }}
                  >
                    {p === "high" ? "Hög prio" : p === "medium" ? "Medium" : "Låg prio"}
                  </button>
                ))}
              </div>
            </div>
            <button onClick={onClose} aria-label="Stäng" style={{ background: "none", border: "none", fontSize: 22, color: "#78716C", cursor: "pointer", padding: "4px 8px" }}>
              ✕
            </button>
          </div>
        </header>

        <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px" }}>
          {/* Stage controls */}
          <section style={{ marginBottom: 24 }}>
            <SectionHeader>Stadium</SectionHeader>
            <div style={{ padding: 14, background: "#FAFAF9", border: "1px solid #E7E5E4", borderRadius: 10 }}>
              <StepperBar stages={BUYER_STAGES} furthestIdx={currentIdx} />
              {advanceHint && (
                <div style={{ marginTop: 10, padding: "8px 10px", background: "#FEF3C7", border: "1px solid #FDE68A", borderRadius: 6, fontSize: 12, color: "#92400E" }}>
                  💡 {advanceHint}
                </div>
              )}
              <div style={{ display: "flex", gap: 8, marginTop: 14, flexWrap: "wrap" }}>
                <button onClick={revert} disabled={!canRevert} style={btnStyle(!canRevert)}>← Backa</button>
                <button onClick={advance} disabled={!canAdvance} style={btnStyle(!canAdvance, true)}>Nästa steg →</button>
                <div style={{ flex: 1 }} />
                <button onClick={() => setTerminal("declined")} disabled={buyer.is_terminal} style={btnStyle(buyer.is_terminal, false, "#DC2626")}>Avböj</button>
                <button onClick={() => setTerminal("withdrawn")} disabled={buyer.is_terminal} style={btnStyle(buyer.is_terminal, false, "#DC2626")}>Drog sig ur</button>
              </div>
            </div>
          </section>

          {/* Contact */}
          <section style={{ marginBottom: 24 }}>
            <SectionHeader>Kontakt</SectionHeader>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <Field label="Kontaktperson (för- och efternamn) *">
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Anders Andersson"
                  style={inputStyle}
                />
              </Field>
              <Field label="Personnummer (frivilligt)">
                <input
                  value={personnummer}
                  onChange={(e) => setPersonnummer(e.target.value)}
                  placeholder="19700101-1234"
                  style={inputStyle}
                />
              </Field>
              <Field label="Telefon">
                <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+46 70 123 45 67" style={inputStyle} />
              </Field>
              <Field label="E-post" wide>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="buyer@example.com" style={inputStyle} />
              </Field>
            </div>
            <div style={{ fontSize: 12, color: "#78716C", marginTop: 6 }}>
              För- och efternamn krävs innan NDA/LOI kan skickas. Personnummer är frivilligt
              och visas efter namnet i signerade dokument: <code>Anders Andersson (19700101-1234)</code>.
            </div>
            <button onClick={saveContact} disabled={savingContact} style={{ ...btnStyle(savingContact), marginTop: 10 }}>
              {savingContact ? "Sparar…" : "Spara kontaktuppgifter"}
            </button>
          </section>

          {/* Portal invite */}
          <section style={{ marginBottom: 24 }}>
            <SectionHeader>Köparportal</SectionHeader>
            <div style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              padding: 12,
              background: buyer.has_dataroom_access ? "#F0FDF4" : "#FAFAF9",
              border: `1px solid ${buyer.has_dataroom_access ? "#BBF7D0" : "#E7E5E4"}`,
              borderRadius: 8,
            }}>
              <div style={{ flex: 1, fontSize: 13, color: "#1C1917" }}>
                {buyer.has_dataroom_access
                  ? "Köparen har åtkomst till köparportalen (datarum, PM, DD)."
                  : "Köparen har ännu inte fått inloggning till köparportalen."}
              </div>
              <button onClick={onInviteToPortal} disabled={!buyer.contact_email} style={btnStyle(!buyer.contact_email, true)}>
                {buyer.has_dataroom_access ? "Skicka igen" : "Skicka inbjudan"}
              </button>
            </div>
          </section>

          {/* Skicka PM */}
          <section style={{ marginBottom: 24 }}>
            <SectionHeader>Skicka PM</SectionHeader>
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              gap: 12, padding: "10px 12px",
              background: buyer.im_sent_at ? "#F0FDF4" : "#FAFAF9",
              border: `1px solid ${buyer.im_sent_at ? "#BBF7D0" : "#E7E5E4"}`,
              borderRadius: 8,
            }}>
              <div style={{ flex: 1, fontSize: 13, color: "#1C1917" }}>
                {buyer.im_sent_at
                  ? `PM skickat ${new Date(buyer.im_sent_at).toLocaleDateString("sv-SE")}.`
                  : "Skickar PM via e-post och låser upp det i köparportalen. Kräver att PM är färdigställt på PM-sidan."}
              </div>
              <button onClick={onSendPM} disabled={!buyer.contact_email} style={btnStyle(!buyer.contact_email, true)}>
                {buyer.im_sent_at ? "Skicka igen" : "Skicka PM"}
              </button>
            </div>
          </section>

          {/* Signing — split per document_type so NDA and LOI live in
              their own sections with their own dropdowns + status lists.
              SPA / DD / custom templates land in the catch-all subsection. */}
          {(() => {
            const ndaTemplates = signingTemplates.filter((t) => t.document_type === "nda");
            const loiTemplates = signingTemplates.filter((t) => t.document_type === "loi");
            const otherTemplates = signingTemplates.filter(
              (t) => t.document_type !== "nda" && t.document_type !== "loi"
            );
            const ndaDocs = signingDocs.filter((d) => d.document_type === "nda");
            const loiDocs = signingDocs.filter((d) => d.document_type === "loi");
            const otherDocs = signingDocs.filter(
              (d) => d.document_type !== "nda" && d.document_type !== "loi"
            );

            const renderTypeBlock = (typeKey, label, templates, docs, helpLink) => (
              <section style={{ marginBottom: 24 }} key={typeKey}>
                <SectionHeader>{label}</SectionHeader>
                {!buyer.contact_email && (
                  <div style={{ padding: "10px 12px", background: "#FEF3C7", border: "1px solid #FDE68A", borderRadius: 8, fontSize: 12, color: "#92400E", marginBottom: 10 }}>
                    Lägg till en e-postadress ovan för att skicka dokument för signering.
                  </div>
                )}

                {buyer.contact_email && templates.length > 0 && (
                  <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
                    <select
                      value={selectedTemplateByType[typeKey] || ""}
                      onChange={(e) => setSelectedTemplateByType((s) => ({ ...s, [typeKey]: e.target.value }))}
                      style={{ ...inputStyle, flex: 1 }}
                    >
                      <option value="">Välj mall…</option>
                      {templates.map((t) => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                      ))}
                    </select>
                    <button
                      onClick={() => {
                        const tid = selectedTemplateByType[typeKey];
                        if (tid) {
                          onSendSigning(tid);
                          setSelectedTemplateByType((s) => ({ ...s, [typeKey]: "" }));
                        }
                      }}
                      disabled={!selectedTemplateByType[typeKey]}
                      style={btnStyle(!selectedTemplateByType[typeKey], true)}
                    >
                      Skicka för signering
                    </button>
                  </div>
                )}

                {buyer.contact_email && templates.length === 0 && (
                  <div style={{ fontSize: 12, color: "#78716C", fontStyle: "italic" }}>
                    Inga {label.toLowerCase()}-mallar uppladdade.{" "}
                    {helpLink}
                  </div>
                )}

                {docs.length > 0 && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {docs.map((d) => (
                      <div key={d.id} style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        padding: "10px 12px",
                        background: d.status === "signed" ? "#F0FDF4" : "#FAFAF9",
                        border: `1px solid ${d.status === "signed" ? "#BBF7D0" : "#E7E5E4"}`,
                        borderRadius: 8,
                      }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: "#1C1917", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {d.name || d.document_type}
                          </div>
                          <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 2, flexWrap: "wrap" }}>
                            <span style={{ fontSize: 11, color: "#78716C" }}>{d.status_label || d.status}</span>
                            {d.viewed_at && d.status !== "signed" && (
                              <span
                                title={`Köparen öppnade dokumentet ${new Date(d.viewed_at).toLocaleString("sv-SE")}${d.view_count > 1 ? ` • ${d.view_count} visningar` : ""}`}
                                style={{ fontSize: 10, fontWeight: 600, color: "#92400E", background: "#FEF3C7", padding: "2px 6px", borderRadius: 4 }}
                              >
                                Sedd
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            );

            const settingsLink = (
              <Link
                to={`/settings/${company}/#nda`}
                style={{ color: "#1C1917", textDecoration: "underline", fontStyle: "normal", fontWeight: 600 }}
              >
                Lägg till mall i inställningar →
              </Link>
            );

            return (
              <>
                {renderTypeBlock("nda", "Sekretessavtal (NDA)", ndaTemplates, ndaDocs, settingsLink)}
                {renderTypeBlock("loi", "Avsiktsförklaring (LOI)", loiTemplates, loiDocs, settingsLink)}
                {(otherTemplates.length > 0 || otherDocs.length > 0) &&
                  renderTypeBlock("other", "Övriga signeringar", otherTemplates, otherDocs, settingsLink)}
              </>
            );
          })()}

          {/* Offer */}
          <section style={{ marginBottom: 24 }}>
            <SectionHeader>Bud</SectionHeader>
            <div style={{ display: "flex", gap: 8 }}>
              <input type="number" value={offerAmount} onChange={(e) => setOfferAmount(e.target.value)} placeholder="Belopp i SEK" style={{ ...inputStyle, flex: 1 }} />
              <button onClick={saveOffer} style={btnStyle(false)}>Spara bud</button>
            </div>
          </section>

          {/* Notes */}
          <section style={{ marginBottom: 24 }}>
            <SectionHeader>Anteckningar</SectionHeader>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={4} placeholder="Interna anteckningar…" style={{ ...inputStyle, resize: "vertical", minHeight: 80, fontFamily: "inherit" }} />
            <button onClick={saveNotes} style={{ ...btnStyle(false), marginTop: 8 }}>Spara anteckningar</button>
          </section>

          {/* Activity feed (per-buyer, broker-only) */}
          <section style={{ marginBottom: 24 }}>
            <SectionHeader>Aktivitet</SectionHeader>
            {activityLoading && <div style={{ color: "#78716C", fontSize: 12 }}>Laddar…</div>}
            {!activityLoading && activity.length === 0 && (
              <div style={{ color: "#A8A29E", fontSize: 12, fontStyle: "italic" }}>Ingen aktivitet ännu.</div>
            )}
            <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
              {activity.map((e, i) => (
                <div key={i} style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 10,
                  padding: "10px 0",
                  borderBottom: i < activity.length - 1 ? "1px solid #E7E5E4" : "none",
                }}>
                  <div style={{ width: 6, height: 6, borderRadius: "50%", marginTop: 7, flexShrink: 0, background: "#57534E" }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, color: "#1C1917", fontWeight: 500 }}>{e.title}</div>
                    {e.description && <div style={{ fontSize: 11, color: "#78716C", marginTop: 2 }}>{e.description}</div>}
                    {e.at && (
                      <div style={{ fontSize: 10, color: "#A8A29E", marginTop: 2 }}>
                        {new Date(e.at).toLocaleString("sv-SE", { dateStyle: "short", timeStyle: "short" })}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Danger */}
          <section style={{ marginTop: 32, paddingTop: 16, borderTop: "1px solid #E7E5E4" }}>
            <button onClick={onRemove} style={{ ...btnStyle(false), background: "#fff", color: "#DC2626", border: "1px solid #FECACA" }}>
              Ta bort köpare
            </button>
          </section>
        </div>
      </aside>
      <style>{`
        @keyframes plFade { from { opacity: 0; } to { opacity: 1; } }
        @keyframes plSlide { from { transform: translateX(100%); } to { transform: translateX(0); } }
      `}</style>
    </>
  );
}

/* ─────────────────────────────────────────────────────────
   SCB Import Modal — search + bulk-pick prospects
───────────────────────────────────────────────────────── */

function ImportScbModal({ existingNames, onImport, onClose }) {
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [filters, setFilters] = useState({});
  const [countResult, setCountResult] = useState(null);
  const [counting, setCounting] = useState(false);
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [importing, setImporting] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [error, setError] = useState("");

  // Load category filters
  useEffect(() => {
    request("/retriever/prospect/categories/")
      .then((data) => setCategories(Array.isArray(data) ? data : []))
      .catch(() => setError("Kunde inte ladda filter."))
      .finally(() => setCategoriesLoading(false));
  }, []);

  // Escape closes
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  const hasFilters = Object.keys(filters).some((k) => filters[k]);
  const activeFilters = Object.fromEntries(
    Object.entries(filters).filter(([_, v]) => v)
  );

  async function handleCount() {
    if (!hasFilters) return;
    setCounting(true);
    setError("");
    try {
      const d = await request("/retriever/prospect/count/", {
        method: "POST",
        body: { filters: activeFilters },
      });
      setCountResult(d.count);
    } catch (e) {
      setError(e?.message || "Kunde inte räkna.");
    }
    setCounting(false);
  }

  async function handleSearch(p = 1) {
    if (!hasFilters) return;
    setSearching(true);
    setError("");
    try {
      const d = await request("/retriever/prospect/search/", {
        method: "POST",
        body: { filters: activeFilters, page: p },
      });
      setResults(d.results || []);
      setCountResult(d.count);
      setPage(p);
      setTotalPages(d.count ? Math.ceil(d.count / (d.page_size || 2000)) : 0);
    } catch (e) {
      setError(e?.message || "Sökning misslyckades.");
    }
    setSearching(false);
  }

  function toggleSelect(orgnr) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(orgnr)) next.delete(orgnr);
      else next.add(orgnr);
      return next;
    });
  }

  function selectAll() {
    const ids = results
      .filter((r) => !existingNames.has((r.name || "").toLowerCase()))
      .map((r) => r.org_number || r.name);
    setSelected(new Set(ids));
  }

  function clearSelection() {
    setSelected(new Set());
  }

  async function handleImport() {
    const toImport = results.filter((r) => selected.has(r.org_number || r.name));
    if (!toImport.length) return;
    setImporting(true);
    try {
      await onImport(toImport);
      // Success confirmation comes from the imported rows appearing in the
      // pipeline list (caller refreshes after onClose).
      onClose();
    } finally {
      setImporting(false);
    }
  }

  const selectedCount = selected.size;
  const newResults = results.filter((r) => !existingNames.has((r.name || "").toLowerCase()));
  const duplicateCount = results.length - newResults.length;

  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.5)",
          zIndex: 1000,
        }}
      />
      <div
        role="dialog"
        aria-modal="true"
        style={{
          position: "fixed",
          top: "5vh",
          left: "50%",
          transform: "translateX(-50%)",
          width: "min(1000px, 95vw)",
          maxHeight: "90vh",
          background: "#fff",
          borderRadius: 16,
          boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
          zIndex: 1001,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <header style={{
          padding: "20px 24px",
          borderBottom: "1px solid #E7E5E4",
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
        }}>
          <div>
            <div style={{ fontSize: 11, color: "#78716C", letterSpacing: 0.5, marginBottom: 4 }}>
              PROSPEKTERING
            </div>
            <h2 style={{ fontSize: 20, fontWeight: 600, color: "#1C1917", margin: 0 }}>
              Hitta köpare
            </h2>
            <p style={{ fontSize: 12, color: "#78716C", margin: "4px 0 0" }}>
              Search the SCB company register for potential buyers. Companies with ad opt-out are excluded.
            </p>
          </div>
          <button
            onClick={onClose}
            style={{ background: "none", border: "none", fontSize: 22, color: "#78716C", cursor: "pointer" }}
            aria-label="Stäng"
          >
            ✕
          </button>
        </header>

        <div style={{ flex: 1, overflow: "auto", padding: "16px 24px" }}>
          {/* Filters */}
          <div style={{ marginBottom: 16 }}>
            <SectionHeader>Filter</SectionHeader>
            {categoriesLoading ? (
              <div style={{ color: "#78716C", fontSize: 13 }}>Laddar filter…</div>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 10 }}>
                {categories.map((cat) => (
                  <label key={cat.code} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    <span style={{ fontSize: 11, fontWeight: 600, color: "#78716C" }}>{cat.name}</span>
                    <select
                      value={filters[cat.code] || ""}
                      onChange={(e) => {
                        const val = e.target.value;
                        setFilters((prev) => {
                          const next = { ...prev };
                          if (val) next[cat.code] = val;
                          else delete next[cat.code];
                          return next;
                        });
                        setCountResult(null);
                        setResults([]);
                      }}
                      style={inputStyle}
                    >
                      <option value="">Alla</option>
                      {(cat.values || []).map((v) => (
                        <option key={v.code} value={v.code}>
                          {v.text || v.code}
                        </option>
                      ))}
                    </select>
                  </label>
                ))}
              </div>
            )}

            <div style={{ display: "flex", gap: 8, marginTop: 12, alignItems: "center" }}>
              <button
                onClick={handleCount}
                disabled={!hasFilters || counting}
                style={btnStyle(!hasFilters || counting)}
              >
                {counting ? "Räknar…" : "Räkna träffar"}
              </button>
              <button
                onClick={() => handleSearch(1)}
                disabled={!hasFilters || searching}
                style={btnStyle(!hasFilters || searching, true)}
              >
                {searching ? "Söker…" : "Sök"}
              </button>
              {countResult != null && (
                <span style={{ fontSize: 13, color: "#57534E" }}>
                  <strong>{countResult.toLocaleString("sv-SE")}</strong> matchande bolag
                </span>
              )}
              <div style={{ flex: 1 }} />
              <button
                onClick={() => { setFilters({}); setCountResult(null); setResults([]); clearSelection(); }}
                style={btnStyle(false)}
              >
                Rensa
              </button>
            </div>
          </div>

          {error && (
            <div style={{ padding: 10, background: "#FEE2E2", color: "#991B1B", borderRadius: 8, fontSize: 13, marginBottom: 12 }}>
              {error}
            </div>
          )}

          {/* Results */}
          {results.length > 0 && (
            <>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                <SectionHeader>Resultat</SectionHeader>
                <div style={{ flex: 1 }} />
                <button onClick={selectAll} style={btnStyle(false)}>Markera alla ({newResults.length})</button>
                <button onClick={clearSelection} style={btnStyle(!selectedCount)}>Rensa</button>
                {duplicateCount > 0 && (
                  <span style={{ fontSize: 11, color: "#D97706", fontStyle: "italic" }}>
                    {duplicateCount} finns redan i pipelinen
                  </span>
                )}
              </div>

              <div style={{ border: "1px solid #E7E5E4", borderRadius: 10, overflow: "hidden" }}>
                <div style={{ maxHeight: 380, overflowY: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                    <thead style={{ background: "#FAFAF9", position: "sticky", top: 0 }}>
                      <tr>
                        <th style={{ padding: "8px 10px", textAlign: "left", width: 32 }}></th>
                        <th style={{ padding: "8px 10px", textAlign: "left", fontWeight: 600, color: "#78716C" }}>Org.nr</th>
                        <th style={{ padding: "8px 10px", textAlign: "left", fontWeight: 600, color: "#78716C" }}>Namn</th>
                        <th style={{ padding: "8px 10px", textAlign: "left", fontWeight: 600, color: "#78716C" }}>Bransch</th>
                        <th style={{ padding: "8px 10px", textAlign: "left", fontWeight: 600, color: "#78716C" }}>Ort</th>
                        <th style={{ padding: "8px 10px", textAlign: "left", fontWeight: 600, color: "#78716C" }}>Anst.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {results.map((r) => {
                        const id = r.org_number || r.name;
                        const isDuplicate = existingNames.has((r.name || "").toLowerCase());
                        const isSelected = selected.has(id);
                        return (
                          <tr
                            key={id}
                            onClick={() => !isDuplicate && toggleSelect(id)}
                            style={{
                              borderTop: "1px solid #F5F5F4",
                              cursor: isDuplicate ? "not-allowed" : "pointer",
                              background: isSelected ? "#F0FDF4" : isDuplicate ? "#FAFAF9" : "#fff",
                              opacity: isDuplicate ? 0.5 : 1,
                            }}
                          >
                            <td style={{ padding: "8px 10px" }}>
                              <input
                                type="checkbox"
                                checked={isSelected}
                                disabled={isDuplicate}
                                readOnly
                              />
                            </td>
                            <td style={{ padding: "8px 10px", fontFamily: "monospace", color: "#57534E" }}>
                              {r.org_number}
                            </td>
                            <td style={{ padding: "8px 10px", color: "#1C1917", fontWeight: 500 }}>
                              {r.name}
                              {isDuplicate && <span style={{ fontSize: 10, color: "#D97706", marginLeft: 6 }}>(finns redan)</span>}
                            </td>
                            <td style={{ padding: "8px 10px", color: "#78716C" }}>
                              {r.sni_text || r.sni_code || "—"}
                            </td>
                            <td style={{ padding: "8px 10px", color: "#78716C" }}>
                              {[r.municipality, r.county].filter(Boolean).join(", ") || "—"}
                            </td>
                            <td style={{ padding: "8px 10px", color: "#78716C" }}>
                              {r.num_employees || "—"}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {totalPages > 1 && (
                <div style={{ display: "flex", gap: 8, marginTop: 10, justifyContent: "center", alignItems: "center" }}>
                  <button onClick={() => handleSearch(page - 1)} disabled={page <= 1 || searching} style={btnStyle(page <= 1 || searching)}>
                    ← Föregående
                  </button>
                  <span style={{ fontSize: 12, color: "#78716C" }}>Sida {page} av {totalPages}</span>
                  <button onClick={() => handleSearch(page + 1)} disabled={page >= totalPages || searching} style={btnStyle(page >= totalPages || searching)}>
                    Nästa →
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <footer style={{
          padding: "14px 24px",
          borderTop: "1px solid #E7E5E4",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
        }}>
          <div style={{ fontSize: 13, color: "#57534E" }}>
            {selectedCount > 0
              ? <><strong>{selectedCount}</strong> {selectedCount === 1 ? "bolag" : "bolag"} markerade</>
              : "Inga bolag markerade"}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={onClose} style={btnStyle(false)}>Avbryt</button>
            <button
              onClick={handleImport}
              disabled={selectedCount === 0 || importing}
              style={btnStyle(selectedCount === 0 || importing, true)}
            >
              {importing ? "Importerar…" : `Importera ${selectedCount || ""}`}
            </button>
          </div>
        </footer>
      </div>
    </>
  );
}

/* ─────────────────────────────────────────────────────────
   Primitives
───────────────────────────────────────────────────────── */

function SectionHeader({ children }) {
  return (
    <div style={{ fontSize: 11, fontWeight: 700, color: "#78716C", letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 10 }}>
      {children}
    </div>
  );
}

function Field({ label, children, wide }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4, gridColumn: wide ? "1 / -1" : undefined }}>
      <span style={{ fontSize: 11, fontWeight: 600, color: "#78716C" }}>{label}</span>
      {children}
    </label>
  );
}

const inputStyle = {
  width: "100%",
  padding: "8px 12px",
  fontSize: 13,
  border: "1px solid #E7E5E4",
  borderRadius: 8,
  outline: "none",
  background: "#fff",
  color: "#1C1917",
  fontFamily: "inherit",
  boxSizing: "border-box",
};

function btnStyle(disabled, primary, color) {
  return {
    padding: "8px 14px",
    fontSize: 13,
    fontWeight: 600,
    border: primary ? "none" : `1px solid ${color || "#D6D3D1"}`,
    borderRadius: 8,
    cursor: disabled ? "not-allowed" : "pointer",
    background: primary ? "#1C1917" : "#fff",
    color: primary ? "#fff" : color || "#57534E",
    opacity: disabled ? 0.5 : 1,
    transition: "all 0.15s",
  };
}

function viewToggleStyle(active) {
  return {
    padding: "6px 12px",
    fontSize: 12,
    fontWeight: 600,
    border: "none",
    borderRadius: 6,
    cursor: "pointer",
    background: active ? "#fff" : "transparent",
    color: active ? "#1C1917" : "#78716C",
    boxShadow: active ? "0 1px 3px rgba(0,0,0,0.06)" : "none",
  };
}

